import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import Sidebar from "../Dashboard/Sidebar";
import Topbar from "../Dashboard/Topbar";
import Loader from "../../components/Loader";
import { ToastContainer, toast } from "react-toastify";
import {
  createOrUpdateEducationalResource,
  EducationalResourceData,
  getEducationalResourceById,
  deleteEducationalResourceVideo,
} from "../../api/educationalResource";
import { getClinicsByUser } from "../../api/clinic";
import { useNavigate, useParams } from "react-router-dom";
import ConfirmModal from "../../components/ConfirmModal";

const MAX_VIDEO_SIZE_MB = 10;
const formatMB = (bytes: number) => (bytes / (1024 * 1024)).toFixed(1);

const EducationalResourceForm: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [clinics, setClinics] = useState<{ id: string; name: string }[]>([]);
  const fetchedClinicsRef = useRef(false);
  const { id } = useParams();
  const lastLoadedIdRef = useRef<string | null>(null);
  const navigate = useNavigate();

  const [formData, setFormData] = useState<EducationalResourceData>({
    title: "",
    description: "",
    category: "BRACES",
    clinicId: "",
    replaceIndex: 0,
    videolink: "",
  });
  const [errors, setErrors] = useState<{ title?: string; clinicId?: string }>(
    {}
  );
  const [videos, setVideos] = useState<File[]>([]);
  const [isVideosModalOpen, setIsVideosModalOpen] = useState(false);
  const [existingVideos, setExistingVideos] = useState<string[]>([]);
  const [removedExistingUrls, setRemovedExistingUrls] = useState<string[]>([]);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState<string | null>(null);
  const [videoMode, setVideoMode] = useState<"link" | "device">("link");
  const [showVideoDeleteConfirm, setShowVideoDeleteConfirm] = useState(false);

  useEffect(() => {
    if (fetchedClinicsRef.current) return;
    fetchedClinicsRef.current = true;
    (async () => {
      try {
        const res = await getClinicsByUser();
        const list = res?.data?.data || res?.data || [];
        const mapped = Array.isArray(list)
          ? list.map((c: any) => ({
            id: c.id || c._id || c.clinicId,
            name: c.name,
          }))
          : [];
        setClinics(mapped);
        if (mapped.length > 0) {
          setFormData((prev) => ({ ...prev, clinicId: mapped[0].id }));
        }
      } catch (e) {
        // ignore silently, allow manual selection if needed
      }
    })();
  }, []);

  useEffect(() => {
    const fetchExisting = async () => {
      if (!id) return;
      if (lastLoadedIdRef.current === id) return; // guard against StrictMode double-call
      lastLoadedIdRef.current = id;
      setLoading(true);
      try {
        const res = await getEducationalResourceById(id);
        const data = res?.data?.data || res?.data || {};
        const resolvedVideoLink = data.videolink || data.videoLink || "";
        setFormData({
          id: data.id || id,
          title: data.title || "",
          description: data.description || "",
          category: data.category || "BRACES",
          clinicId: data.clinicId || formData.clinicId || "",
          replaceIndex: 0,
          videolink: resolvedVideoLink,
        });
        const urls: string[] = (() => {
          const single = data?.video?.url;
          const arr = data?.videos;
          if (Array.isArray(arr)) {
            return arr
              .map((v: any) => (typeof v === "string" ? v : v?.url))
              .filter((u: any) => typeof u === "string" && !!u);
          }
          if (typeof single === "string" && single) return [single];
          return [];
        })();
        setExistingVideos(urls);
        setVideoMode((prev) => {
          if (resolvedVideoLink) return "link";
          if (urls.length > 0) return "device";
          return prev;
        });
      } catch (e) {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    fetchExisting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleInput = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleVideosChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length > 0) {
      setVideoMode("device");
      // Check if there are already videos (new or existing)
      const hasExistingVideos = existingVideos.filter(
        (u) => !removedExistingUrls.includes(u)
      ).length > 0;
      const hasNewVideos = videos.length > 0;

      if (hasExistingVideos || hasNewVideos) {
        toast.error("Please remove the previous video before uploading a new one");
        // Reset the input
        e.target.value = '';
        return;
      }

      // Only allow one video
      if (files.length > 1) {
        toast.error("Please upload only one video at a time");
        e.target.value = '';
        return;
      }

      const file = files[0];
      const maxBytes = MAX_VIDEO_SIZE_MB * 1024 * 1024;
      if (file.size > maxBytes) {
        toast.error(
          `Video size is too large. Max allowed is ${MAX_VIDEO_SIZE_MB}MB (selected ${formatMB(
            file.size
          )}MB).`
        );
        e.target.value = '';
        return;
      }

      setVideos([file]);
    }
  };

  const handleVideoModeChange = (mode: "link" | "device") => {
    setVideoMode(mode);
  };

  const removeVideoAt = (idx: number) => {
    setVideos((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleDeleteExistingVideo = async () => {
    if (!id) return;
    setLoading(true);
    try {
      await deleteEducationalResourceVideo(id);
      toast.success("Video deleted successfully");
      setExistingVideos([]);
      setShowVideoDeleteConfirm(false);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to delete video");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors: typeof errors = {};
    if (!formData.title.trim()) nextErrors.title = "Title is required";
    if (!formData.clinicId) nextErrors.clinicId = "Clinic is required";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setLoading(true);
    try {
      const retainVideoUrls =
        videoMode === "device"
          ? existingVideos.filter((u) => !removedExistingUrls.includes(u))
          : [];
      const payload: EducationalResourceData = {
        ...formData,
        videolink: formData.videolink?.trim() || "",
      };
      const hasNewUpload = videoMode === "device" && videos.length > 0;

      // For backends that require existing videos echoed back
      if (retainVideoUrls.length > 0) {
        // Some APIs expect a single object instead of array when only one exists
        try {
          (payload as any).video =
            retainVideoUrls.length === 1
              ? { url: retainVideoUrls[0] }
              : undefined;
        } catch { }
      }

      if (videoMode === "link") {
        if (!payload.videolink) {
          toast.error("Upload a video file or provide a video link.");
          setLoading(false);
          return;
        }
      } else {
        const hasRetained = retainVideoUrls.length > 0;
        if (!hasNewUpload && !hasRetained) {
          toast.error("Please upload a video file.");
          setLoading(false);
          return;
        }
        payload.videolink = hasNewUpload
          ? ""
          : retainVideoUrls[0] || "";
      }
      await createOrUpdateEducationalResource(payload, hasNewUpload ? videos : []);
      toast.success("Saved successfully");
      navigate("/educational-resources");
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <ToastContainer position="top-right" autoClose={3000} />
      {loading && <Loader />}

      <Sidebar
        isOpen={isSidebarOpen}
        toggle={() => setIsSidebarOpen(!isSidebarOpen)}
      />

      <div className="flex-1 flex flex-col">
        <Topbar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

        <main className="p-4 md:p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-6xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="w-full rounded-3xl shadow-2xl bg-white p-8"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                  {id
                    ? "Edit Educational Hub"
                    : "Create Educational Hub"}
                </h2>
                <button
                  className="px-4 py-1 rounded-xl bg-gray-100 text-gray-800 border border-gray-200 hover:bg-gray-200 transition flex items-center gap-2"
                  onClick={() => window.history.back()}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-4 h-4"
                    aria-hidden="true"
                  >
                    <path d="M10 19l-7-7 7-7" />
                    <path d="M3 12h18" />
                  </svg>
                  Back
                </button>
              </div>

              <form
                noValidate
                onSubmit={onSubmit}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                   Video Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInput}
                    className={`w-full transition ${errors.title
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : ""
                      }`}
                    placeholder="Enter title"
                  />
                  {errors.title && (
                    <p className="text-red-500 text-sm mt-1">{errors.title}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Category
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInput}
                    className="w-full transition"
                  >
                    <option value="BRACES">Braces</option>
                    <option value="ALIGNERS">Aligners</option>
                    <option value="PROCEDURES">Procedures</option>
                    <option value="DENTAL_CARE">Dental Care</option>
                    <option value="RECOMMENDATIONS">Recommendations</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Clinic <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="clinicId"
                    value={formData.clinicId}
                    onChange={handleInput}
                    className={`w-full transition ${errors.clinicId
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : ""
                      }`}
                  >
                    <option value="" disabled selected>
                      Select clinic
                    </option>
                    {clinics.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  {errors.clinicId && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.clinicId}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700">
                    Video Source
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {[
                      { label: "Upload Video from Link", value: "link" },
                      { label: "Upload Video from Device", value: "device" },
                    ].map((option) => (
                      <label
                        key={option.value}
                        className="flex items-center gap-2 text-sm cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="videoMode"
                          value={option.value}
                          checked={videoMode === option.value}
                          onChange={() =>
                            handleVideoModeChange(
                              option.value as "link" | "device"
                            )
                          }
                          className="text-blue-600 focus:ring-blue-500"
                        />
                        <span
                          className={
                            videoMode === option.value
                              ? "text-blue-700 font-medium"
                              : "text-gray-700"
                          }
                        >
                          {option.label}
                        </span>
                      </label>
                    ))}
                  </div>

                  {videoMode === "link" ? (
                    <div className="space-y-1">
                      <label className="text-sm font-semibold text-gray-700">
                        Video Link
                      </label>
                      <div className="relative">
                        <input
                          type="url"
                          name="videolink"
                          value={formData.videolink}
                          onChange={handleInput}
                          placeholder="https://example.com/video.mp4"
                          className="w-full pl-3 pr-10 transition"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-500 text-xs uppercase tracking-wide">
                          link
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        Paste a hosted video URL if you prefer linking instead of
                        uploading a file.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">
                        Upload Video
                      </label>
                      <div className="mt-1 flex items-center gap-3">
                        <input
                          type="file"
                          id="videos"
                          accept="video/*"
                          onChange={handleVideosChange}
                          className="hidden"
                        />
                        <label
                          htmlFor="videos"
                          className="px-2 py-1 rounded-lg bg-blue-600 text-white cursor-pointer hover:bg-blue-700 transition"
                        >
                          Upload Files
                        </label>
                        {(() => {
                          const selectedCount =
                            existingVideos.filter(
                              (u) => !removedExistingUrls.includes(u)
                            ).length + videos.length;
                          return selectedCount > 0 ? (
                            <span className="text-sm text-gray-600">
                              {selectedCount} video selected
                            </span>
                          ) : null;
                        })()}
                        {(existingVideos.length > 0 || videos.length > 0) && (
                          <button
                            type="button"
                            onClick={() => setIsVideosModalOpen(true)}
                            className="px-3 py-1 rounded-lg bg-gray-100 text-gray-800 border border-gray-200 hover:bg-gray-200 transition"
                          >
                            View files
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        Choose a video from your device. Only one upload is allowed per
                        resource and the file must be under {MAX_VIDEO_SIZE_MB}MB.
                      </p>
                    </div>
                  )}
                </div>


                {isVideosModalOpen && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl p-6 max-h-[90vh] flex flex-col">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold">
                          Uploaded Videos
                        </h3>
                        <button
                          className="text-gray-600 hover:text-gray-900"
                          onClick={() => {
                            // Clean up object URL if it's a blob URL
                            if (selectedVideoUrl && selectedVideoUrl.startsWith('blob:')) {
                              URL.revokeObjectURL(selectedVideoUrl);
                            }
                            setIsVideosModalOpen(false);
                            setSelectedVideoUrl(null);
                          }}
                        >
                          ✕
                        </button>
                      </div>

                      {selectedVideoUrl ? (
                        <div className="flex-1 flex flex-col">
                          <div className="mb-4">
                            <button
                              type="button"
                              onClick={() => {
                                // Clean up object URL if it's a blob URL
                                if (selectedVideoUrl.startsWith('blob:')) {
                                  URL.revokeObjectURL(selectedVideoUrl);
                                }
                                setSelectedVideoUrl(null);
                              }}
                              className="text-blue-600 hover:underline text-sm"
                            >
                              ← Back to list
                            </button>
                          </div>
                          <div className="flex-1 bg-black rounded-lg overflow-hidden">
                            <video
                              src={selectedVideoUrl}
                              controls
                              className="w-full h-full max-h-[60vh]"
                              autoPlay
                            >
                              Your browser does not support the video tag.
                            </video>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2 flex-1">
                            {videos.length === 0 &&
                              existingVideos.filter(
                                (u) => !removedExistingUrls.includes(u)
                              ).length === 0 ? (
                              <div className="text-sm text-red-500 text-center py-8">
                                No videos uploaded
                              </div>
                            ) : (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {/* New Videos */}
                                {videos.map((file, idx) => (
                                  <div
                                    key={`new-${idx}`}
                                    className="border rounded-lg p-3 flex flex-col gap-2"
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="text-sm text-gray-700 truncate mr-2 flex-1">
                                        {file.name}
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => removeVideoAt(idx)}
                                        className="text-red-600 hover:underline text-sm whitespace-nowrap"
                                      >
                                        Remove
                                      </button>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const videoUrl = URL.createObjectURL(file);
                                        setSelectedVideoUrl(videoUrl);
                                      }}
                                      className="text-blue-600 hover:underline text-sm text-left"
                                    >
                                      View Video
                                    </button>
                                  </div>
                                ))}

                                {/* Existing Videos */}
                                {existingVideos
                                  .filter((u) => !removedExistingUrls.includes(u))
                                  .map((url, idx) => {
                                    const removed = removedExistingUrls.includes(url);
                                    return (
                                      <div
                                        key={`existing-${idx}`}
                                        className={`border rounded-lg p-3 flex flex-col gap-2 ${removed ? "opacity-60" : ""
                                          }`}
                                      >
                                        <div className="flex items-center justify-between">
                                          <div className="text-sm text-blue-600 truncate mr-2 flex-1">
                                            {url.split("/").pop() || `Video ${idx + 1}`}
                                          </div>
                                          <button
                                            type="button"
                                            onClick={() => setShowVideoDeleteConfirm(true)}
                                            className="text-red-600 hover:underline text-sm whitespace-nowrap"
                                          >
                                            Remove
                                          </button>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => setSelectedVideoUrl(url)}
                                          className="text-blue-600 hover:underline text-sm text-left"
                                        >
                                          View Video
                                        </button>
                                      </div>
                                    );
                                  })}
                              </div>
                            )}
                          </div>
                          <div className="mt-5 flex justify-end">
                            <button
                              type="button"
                              onClick={() => {
                                // Clean up object URL if it's a blob URL
                                if (selectedVideoUrl && selectedVideoUrl.startsWith('blob:')) {
                                  URL.revokeObjectURL(selectedVideoUrl);
                                }
                                setIsVideosModalOpen(false);
                                setSelectedVideoUrl(null);
                              }}
                              className="px-4 py-2 rounded-xl bg-green-600 text-white hover:bg-green-700 transition"
                            >
                              Done
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                  Video Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInput}
                    rows={4}
                    className="w-full transition"
                    placeholder="Enter description"
                  />
                </div>

                <div className="md:col-span-2 flex justify-end gap-3 mt-4">
                  <a
                    href="/educational-resources"
                    className="px-5 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                  >
                    Cancel
                  </a>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-5 py-2 rounded-xl   text-white   transition disabled:opacity-60"
                  >
                    {loading ? "Saving..." : "Save Resource"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        </main>
      </div>

      <ConfirmModal
        isOpen={showVideoDeleteConfirm}
        title="Delete Video"
        message="Are you sure you want to delete this uploaded video?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteExistingVideo}
        onCancel={() => setShowVideoDeleteConfirm(false)}
        loading={loading}
        zIndex={100}
      />
    </div>
  );
};

export default EducationalResourceForm;
